/**
 * @file api-order-items.ts
 * @description API-клиент редактора состава заказа (phase19.15 шаг 2b/4).
 *
 * Зеркалит `apps/api/src/orders/items/order-items.service.ts`:
 *  - GET    /admin/orders/:id/items            — состав (сырые позиции со снапшотом);
 *  - POST   /admin/orders/:id/items            — добавить товар;
 *  - PATCH  /admin/orders/:id/items/:itemId    — изменить количество;
 *  - DELETE /admin/orders/:id/items/:itemId    — убрать позицию.
 *
 * Мутации возвращают пересчитанный состав, но список после них мы всё равно
 * перечитываем: GET отдаёт снапшот целиком (код SKU, GTIN, DataMatrix), а
 * ответ мутации — только то, что нужно для сумм. Один источник правды — сервер.
 * Из ответа берём `changeSummary` (текст для тоста) и `agreementReset`.
 */

import { apiFetch, ApiError } from "./api-client";

// ─── DTO ────────────────────────────────────────────────────────────────────

/** Метка позиции-услуги «Доставка» в снапшоте (константа сервера). */
export const DELIVERY_SERVICE_KIND = "DELIVERY_SERVICE";

/** Позиция заказа как её отдаёт GET /admin/orders/:id/items. */
export interface OrderItemRawDto {
  id: string;
  skuId: string | null;
  productSnapshot: {
    productName?: string;
    skuCode?: string;
    gtin?: string | null;
    markingState?: string;
    /** `DELIVERY_SERVICE` — это доставка, а не товар. */
    kind?: string;
  };
  quantity: string;
  priceGross: string;
  vatRate: number;
  vatAmount: string;
  totalGross: string;
  discountAmount: string;
  markingDatamatrix: string | null;
}

/** Строка состава в ответе редактора (без снапшота — только суммы). */
export interface OrderItemLineDto {
  id: string;
  skuId: string | null;
  name: string;
  quantity: string;
  priceGross: string;
  vatRate: number;
  vatAmount: string;
  discountAmount: string;
  totalGross: string;
  isService: boolean;
}

/** Результат любой правки состава. */
export interface OrderItemsEditResultDto {
  items: OrderItemLineDto[];
  totalGross: string;
  totalVat: string;
  discountAmount: string;
  /**
   * Текст записи в ленту («Состав изменён: … сумма 1 300 → 1 750 ₽»).
   * `null` — правка ничего не изменила (no-op), тост показывать не за что.
   */
  changeSummary: string | null;
  /** Правка отозвала отправленное клиенту предложение по доставке (F30). */
  agreementReset: boolean;
  /**
   * Состав изменился так, что рассчитанная стоимость доставки могла устареть
   * (вес/сумма поехали). Поле опционально: пока сервер его не отдаёт,
   * предупреждение просто не показывается.
   */
  deliveryRecalcNeeded?: boolean;
}

// ─── Запросы ────────────────────────────────────────────────────────────────

export async function listOrderItems(orderId: string): Promise<OrderItemRawDto[]> {
  const res = await apiFetch<{ items: OrderItemRawDto[] }>(
    `/admin/orders/${orderId}/items`,
  );
  return res.items;
}

export function addOrderItem(
  orderId: string,
  payload: { skuId: string; quantity: number },
): Promise<OrderItemsEditResultDto> {
  return apiFetch<OrderItemsEditResultDto>(`/admin/orders/${orderId}/items`, {
    method: "POST",
    json: payload,
  });
}

export function updateOrderItemQuantity(
  orderId: string,
  itemId: string,
  quantity: number,
): Promise<OrderItemsEditResultDto> {
  return apiFetch<OrderItemsEditResultDto>(
    `/admin/orders/${orderId}/items/${itemId}`,
    { method: "PATCH", json: { quantity } },
  );
}

export function removeOrderItem(
  orderId: string,
  itemId: string,
): Promise<OrderItemsEditResultDto> {
  return apiFetch<OrderItemsEditResultDto>(
    `/admin/orders/${orderId}/items/${itemId}`,
    { method: "DELETE" },
  );
}

// ─── Вспомогательное ────────────────────────────────────────────────────────

/** Позиция-услуга «Доставка»: редактору недоступна (у неё свой путь цены). */
export function isDeliveryServiceItem(item: OrderItemRawDto): boolean {
  return item.productSnapshot?.kind === DELIVERY_SERVICE_KIND;
}

/**
 * Количество без хвоста нулей: «2», но «0.5» для весовых позиций.
 * В БД `Decimal(12,3)`, и «2.000 шт» в интерфейсе читается как ошибка.
 */
export function formatOrderItemQuantity(value: string): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return value;
  return String(Number(num.toFixed(3)));
}

/** Название позиции из снапшота — тот же контракт, что у сервера. */
export function orderItemName(item: OrderItemRawDto): string {
  return item.productSnapshot?.productName ?? "—";
}

/**
 * Ошибка редактора человеческим языком.
 *
 * Сервер по всем кодам редактора (`order_items_locked`,
 * `order_item_delivery_service`, `order_items_last_item`,
 * `order_insufficient_stock`) присылает готовое объяснение менеджеру — своих
 * формулировок не выдумываем, иначе интерфейс и 422 разойдутся в словах.
 * Функция нужна ради второго случая: сетевой сбой без `message`.
 */
export function orderItemsErrorText(
  err: unknown,
  fallback = "Не удалось изменить состав",
): string {
  if (err instanceof ApiError) return err.message || fallback;
  return err instanceof Error && err.message ? err.message : fallback;
}
